import { getLogger } from "@expert/logging";
import { HTTPError } from "ky";
import { removeCountryCode } from "@/shared-utils";
import { getCustomerDetailsApi } from "./api";
import type {
    BillingAccountNumberGetCustomerDetailsParams,
    CustomerDetailsPayload,
    CustomerDetailsV2RequestBody,
    GetCustomerDetailsV2Params,
    PhoneNumberGetCustomerDetailsParams,
} from "./types";

const logger = getLogger({
    module: "customerDetails",
    supportTeam: "sales",
});

export const getCustomerDetailsV2 = async (
    params: GetCustomerDetailsV2Params,
): Promise<CustomerDetailsPayload | null> => {
    const { partner, searchType, dispatcher, customerDetailsCallback } = params;
    let jsonPayload: CustomerDetailsV2RequestBody;

    if (isValidPhoneNumberSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            criteria: {
                phoneNumber: removeCountryCode(params.mdn),
            },
        };
    } else if (isValidBillingAccountNumberAndPinSearchType(params)) {
        jsonPayload = {
            partner,
            searchType,
            criteria: {
                billingAccountNumber: params.billingAccountNumber,
                customerVerificationMethod: params.pin ? "PIN" : "BANV",
                pin: params.pin,
            },
        };
    } else {
        return null;
    }

    try {
        const client = getCustomerDetailsApi();

        const apiPath = "v2/customer/details";

        const response = await client.post(apiPath, { json: jsonPayload, retry: 0 }).json<CustomerDetailsPayload[]>();

        const result = getFirstValidCustomerDetail(response);

        if (result === null) {
            return null;
        }

        // only update the MDN if it was provided in the initial request
        if (result.primaryAccountHolder?.mdn && isValidPhoneNumberSearchType(params)) {
            result.primaryAccountHolder.mdn = params.mdn;
        }
        void dispatcher?.dispatchBusinessEvent("CustomerDetailsStats", {
            includesPrimaryAccountHolder: !!result.primaryAccountHolder,
            includesAccountNumber: !!result.primaryAccountHolder?.accountNumber,
            includesDeviceReplacementDetails: !!result.deviceActivation,
            includesPreviousSummary: !!result.previousSummary,
            previousDeviceCount: result.previousDevices?.length ?? 0,
        });

        customerDetailsCallback?.(result);

        return result;
    } catch (err: unknown) {
        if (err instanceof HTTPError) {
            logger.error(`getCustomerDetails API call failed with status: ${err.response.status}`);
            return null;
        }
        logger.error({ err }, `getCustomerDetails  API call failed with exception`);
        return null;
    }
};

export const getFirstValidCustomerDetail = (
    customerDetails: CustomerDetailsPayload[] | null | undefined,
): CustomerDetailsPayload | null => {
    if (!customerDetails || customerDetails.length === 0) {
        return null;
    }

    const validDetails = customerDetails.filter(
        (detail) => detail.primaryAccountHolder !== null && detail.primaryAccountHolder !== undefined,
    );

    if (validDetails.length > 0) {
        return validDetails[0];
    } else {
        return null;
    }
};

function isValidPhoneNumberSearchType(
    params: GetCustomerDetailsV2Params,
): params is PhoneNumberGetCustomerDetailsParams {
    return params.searchType === "phoneNumber" && params.mdn != null;
}

function isValidBillingAccountNumberAndPinSearchType(
    params: GetCustomerDetailsV2Params,
): params is BillingAccountNumberGetCustomerDetailsParams {
    return params.searchType === "billingAccountNumberAndPin" && params.billingAccountNumber != null;
}
